/**
 * `host_session_id` ties a visitor's pageviews together before they are
 * identified. The shell's server mints and sets the cookie (`server/session.ts`,
 * and `vite.config.ts` does the same for local development), so it is already
 * in place by the time any of this runs.
 *
 * The parse below is a deliberate duplicate of `readHostSessionId` in
 * `server/session.ts`. That one takes a request's `Cookie` header, this one
 * reads `document.cookie`, and the two are the same format — but the server
 * file is not somewhere the browser bundle should import from, and rearranging
 * files to share ten lines costs more than the duplication. Two behaviours have
 * to stay matched with it: an exact cookie-name match, and treating a
 * present-but-empty cookie as absent.
 */

const HOST_SESSION_COOKIE = 'host_session_id';

/**
 * Cached once found, because the value does not change within a page: the
 * server sets it on the document's own response and nothing rewrites it.
 *
 * A *missing* cookie is deliberately not cached. That is the state where
 * something is wrong — an environment whose server does not set it, or a test —
 * and re-reading costs nothing at the rate page events happen.
 */
let cached: string | undefined;

export function hostSessionId(): string | undefined {
  if (cached != null) {
    return cached;
  }

  let all: string;
  try {
    all = document.cookie;
  } catch {
    return undefined;
  }

  for (const pair of all.split(';')) {
    const separator = pair.indexOf('=');
    if (separator === -1) {
      continue;
    }
    // Exact name match, so `not_host_session_id` is somebody else's cookie.
    if (pair.slice(0, separator).trim() !== HOST_SESSION_COOKIE) {
      continue;
    }
    const value = pair.slice(separator + 1).trim();
    // A present-but-empty cookie counts as absent rather than as an id every
    // such visitor shares.
    if (value === '') {
      return undefined;
    }
    cached = value;
    return cached;
  }

  return undefined;
}

/** Test seam: the cache is module state, so suites have to clear it. */
export function resetHostSessionForTests(): void {
  cached = undefined;
}
